/**
 * Native keyboard UX for the Capacitor shell.
 *
 * The shell already configures Keyboard `resize: 'native'`, so the webview
 * shrinks above the keyboard and focused form inputs auto-scroll into view.
 * This module adds the two things that native resize alone doesn't do — and,
 * crucially, does NOT add any padding/height math on top of the native resize
 * (which would double-count and push content too far):
 *
 *   1. A `keyboard-open` class on <html> while the keyboard is up, so layout can
 *      opt into native touches. Purely additive — nothing changes by default, so
 *      existing screens can't regress.
 *   2. Re-pins any `[data-keyboard-anchor="bottom"]` scroller (the chat message
 *      list) to its newest content once the keyboard finishes showing, so the
 *      latest messages stay visible directly above the composer.
 *
 * No-ops outside the Capacitor shell (browser + MobiLoud unaffected) and never
 * throws.
 */

import { Keyboard } from "@capacitor/keyboard";
import Honeybadger from "@honeybadger-io/js";
import { isCapacitorEnvironment } from "./detection";

const KEYBOARD_OPEN_CLASS = "keyboard-open";
const BOTTOM_ANCHOR_SELECTOR = '[data-keyboard-anchor="bottom"]';

function reportKeyboardError(error) {
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_keyboard" },
    });
  } catch {
    // reporting must never break the app
  }
}

function setKeyboardOpen(isOpen) {
  const root = document.documentElement;
  if (!root) {
    return;
  }
  root.classList.toggle(KEYBOARD_OPEN_CLASS, isOpen);
}

// Pin bottom-anchored scrollers (the chat message list) to their newest content
// so it stays visible above the composer after the keyboard has animated in.
function pinBottomAnchors() {
  document.querySelectorAll(BOTTOM_ANCHOR_SELECTOR).forEach((el) => {
    el.scrollTop = el.scrollHeight;
  });
}

// Keyboard.addListener is async; surface a rejection instead of letting it
// become an unhandled promise rejection.
function addKeyboardListener(eventName, handler) {
  const result = Keyboard.addListener(eventName, handler);
  if (result && typeof result.catch === "function") {
    result.catch(reportKeyboardError);
  }
}

let registered = false;

/**
 * Wire native keyboard show/hide handling. No-op off-shell; runs once; never
 * throws.
 */
export function initKeyboard() {
  if (!isCapacitorEnvironment() || registered) {
    return;
  }
  registered = true;
  try {
    // Set the class on both will/did show so it's applied even on platforms
    // (Android) that only reliably emit one of the two events.
    addKeyboardListener("keyboardWillShow", () => setKeyboardOpen(true));
    addKeyboardListener("keyboardDidShow", () => {
      setKeyboardOpen(true);
      pinBottomAnchors();
    });
    addKeyboardListener("keyboardWillHide", () => setKeyboardOpen(false));
    addKeyboardListener("keyboardDidHide", () => setKeyboardOpen(false));
  } catch (error) {
    // A synchronous throw means no listener was attached, so it's safe to reset
    // the guard and let a later call retry. (An async per-listener rejection is
    // only reported — we don't reset there, to avoid double-registering the
    // listeners that did attach.)
    registered = false;
    reportKeyboardError(error);
  }
}
